import { fmtDate } from '../utils';
import { useState, useEffect } from 'react';
import { api } from '../api';

function fullName(s) { return s ? `${s.firstName || ''} ${s.lastName || ''}`.trim() : ''; }
function className(c) { return c ? `${c.name} - ${c.section}` : '—'; }

export default function Fees() {
  const [tab, setTab] = useState('payments');
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [feeStructure, setFeeStructure] = useState([]);
  const [loading, setLoading] = useState(true);

  // New payment form
  const [form, setForm] = useState({
    student: { id: '' }, feeStructure: { id: '' },
    amountPaid: '', paymentDate: new Date().toISOString().split('T')[0],
    paymentMode: 'Cash', receiptNo: '', remarks: ''
  });
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    Promise.all([api.getFeePayments(), api.getStudents(), api.getClasses(), api.getFeeStructure()])
      .then(([p, s, c, fs]) => { setPayments(p); setStudents(s); setClasses(c); setFeeStructure(fs); })
      .finally(() => setLoading(false));
  }, []);

  const handlePay = async () => {
    if (!form.student.id || !form.amountPaid || !form.paymentDate) {
      setErr('Student, amount and date are required'); return;
    }
    try {
      await api.createFeePayment({
        ...form,
        student: { id: parseInt(form.student.id) },
        feeStructure: form.feeStructure.id ? { id: parseInt(form.feeStructure.id) } : null,
        amountPaid: parseFloat(form.amountPaid)
      });
      window.location.reload();
    } catch (e) { setErr(e.message); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const totalCollected = payments.reduce((s, p) => s + parseFloat(p.amountPaid || 0), 0);

  if (loading) return <div className="loading-msg">Loading fees…</div>;

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'payments' ? ' active' : ''}`} onClick={() => setTab('payments')}>Payments</button>
        <button className={`tab${tab === 'collect' ? ' active' : ''}`} onClick={() => setTab('collect')}>Collect Fee</button>
        <button className={`tab${tab === 'structure' ? ' active' : ''}`} onClick={() => setTab('structure')}>Fee Structure</button>
      </div>

      {tab === 'payments' && (
        <div>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon green"><i className="fas fa-rupee-sign"></i></div>
              <div>
                <div className="stat-label">Total Collected</div>
                <div className="stat-value">₹{totalCollected.toLocaleString('en-IN')}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon blue"><i className="fas fa-file-invoice"></i></div>
              <div>
                <div className="stat-label">Total Receipts</div>
                <div className="stat-value">{payments.length}</div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Payment History</div>
              <div className="card-subtitle">{payments.length} records</div>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>#</th><th>Receipt No</th><th>Student</th><th>Class</th><th>Amount</th><th>Mode</th><th>Date</th></tr>
                </thead>
                <tbody>
                  {payments.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: 30 }}>No payments found</td></tr>
                  )}
                  {payments.map((p, i) => (
                    <tr key={p.id}>
                      <td>{i + 1}</td>
                      <td><strong>{p.receiptNo || '—'}</strong></td>
                      <td>{fullName(p.student)}</td>
                      <td>{p.student?.classSection ? className(p.student.classSection) : '—'}</td>
                      <td><span className="badge badge-green">₹{Number(p.amountPaid).toLocaleString('en-IN')}</span></td>
                      <td>{p.paymentMode}</td>
                      <td>{fmtDate(p.paymentDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'collect' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Collect Fee Payment</div>
          </div>
          <div className="card-body">
            {err && <div className="error-msg">{err}</div>}
            <div className="form-grid cols-3">
              <div className="form-group">
                <label className="form-label">Student *</label>
                <select className="form-control" value={form.student.id}
                  onChange={e => setForm(f => ({ ...f, student: { id: e.target.value } }))}>
                  <option value="">-- Select Student --</option>
                  {students.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.admissionNo} - {fullName(s)} ({s.classSection ? className(s.classSection) : 'No Class'})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Fee Type</label>
                <select className="form-control" value={form.feeStructure.id}
                  onChange={e => setForm(f => ({ ...f, feeStructure: { id: e.target.value } }))}>
                  <option value="">-- Optional --</option>
                  {feeStructure.map(fs => (
                    <option key={fs.id} value={fs.id}>
                      {fs.classSection ? className(fs.classSection) : '—'} — {fs.feeType} (₹{Number(fs.amount).toLocaleString('en-IN')})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Amount Paid (₹) *</label>
                <input className="form-control" type="number" value={form.amountPaid} onChange={e => set('amountPaid', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Date *</label>
                <input className="form-control" type="date" value={form.paymentDate} onChange={e => set('paymentDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Mode</label>
                <select className="form-control" value={form.paymentMode} onChange={e => set('paymentMode', e.target.value)}>
                  <option value="Cash">Cash</option>
                  <option value="Online">Online</option>
                  <option value="Cheque">Cheque</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Receipt No</label>
                <input className="form-control" value={form.receiptNo} onChange={e => set('receiptNo', e.target.value)} placeholder="Auto or manual" />
              </div>
              <div className="form-group full">
                <label className="form-label">Remarks</label>
                <textarea className="form-control" value={form.remarks} onChange={e => set('remarks', e.target.value)} />
              </div>
            </div>
            <div className="form-actions">
              <button className="btn btn-success" onClick={handlePay}>
                <i className="fas fa-rupee-sign"></i> Collect Payment
              </button>
              {saved && <span className="badge badge-green">Payment recorded!</span>}
            </div>
          </div>
        </div>
      )}

      {tab === 'structure' && (
        <FeeStructureTab classes={classes} feeStructure={feeStructure} setFeeStructure={setFeeStructure} />
      )}
    </div>
  );
}

function FeeStructureTab({ classes, feeStructure, setFeeStructure }) {
  const [form, setForm] = useState({ classSection: { id: '' }, feeType: '', amount: '', academicYear: '2024-25' });
  const [err, setErr] = useState('');

  const handleSave = async () => {
    if (!form.classSection.id || !form.feeType || !form.amount) { setErr('All fields required'); return; }
    try {
      await api.createFeeStructure({
        ...form, classSection: { id: parseInt(form.classSection.id) }, amount: parseFloat(form.amount)
      });
      window.location.reload();
    } catch (e) { setErr(e.message); }
  };

  function clsName(c) { return c ? `${c.name} - ${c.section}` : '—'; }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <div className="card-title">Add Fee Structure</div>
        </div>
        <div className="card-body">
          {err && <div className="error-msg">{err}</div>}
          <div className="form-grid cols-3">
            <div className="form-group">
              <label className="form-label">Class</label>
              <select className="form-control" value={form.classSection.id}
                onChange={e => setForm(f => ({ ...f, classSection: { id: e.target.value } }))}>
                <option value="">-- Select --</option>
                {classes.map(c => <option key={c.id} value={c.id}>{clsName(c)}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Fee Type</label>
              <input className="form-control" value={form.feeType} onChange={e => setForm(f => ({ ...f, feeType: e.target.value }))} placeholder="Tuition, Transport…" />
            </div>
            <div className="form-group">
              <label className="form-label">Amount (₹)</label>
              <input className="form-control" type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Year</label>
              <input className="form-control" value={form.academicYear} onChange={e => setForm(f => ({ ...f, academicYear: e.target.value }))} />
            </div>
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" onClick={handleSave}>
              <i className="fas fa-plus"></i> Add
            </button>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-header">
          <div className="card-title">Fee Structure</div>
          <div className="card-subtitle">{feeStructure.length} entries</div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>#</th><th>Class</th><th>Fee Type</th><th>Amount</th><th>Year</th></tr>
            </thead>
            <tbody>
              {feeStructure.map((fs, i) => (
                <tr key={fs.id}>
                  <td>{i + 1}</td>
                  <td>{fs.classSection ? clsName(fs.classSection) : '—'}</td>
                  <td>{fs.feeType}</td>
                  <td><span className="badge badge-green">₹{Number(fs.amount).toLocaleString('en-IN')}</span></td>
                  <td>{fs.academicYear}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
